import { CaretDown } from '@phosphor-icons/react'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@radix-ui/react-accordion'
import clsx from 'clsx'
import { Fragment, JSX, useState } from 'react'
import Content from '../elements/Content'
import Picture from '../elements/Picture'
import { BardAccordionBox as BardAccordionBoxType } from '@/src/@types/statamic'
import useBreakpointReached from '@/src/hooks/useBreakpointReached'
import trackEvent from '@/src/lib/tracking'

export default function BardAccordionBox({
  accordion_box: accordion,
  fullWidth,
}: BardAccordionBoxType & { fullWidth?: boolean }) {
  const [value, setValue] = useState<string[]>([])
  const mdBreakpointReached = useBreakpointReached('md')

  const sendGTMEvent = (title: string) => {
    // value isn't updated yet
    if (!value.includes(title)) {
      trackEvent({
        event: 'accordion_open',
        params: {
          title,
        },
      })
    }
  }

  return (
    <Accordion
      type="multiple"
      value={value}
      onValueChange={setValue}
      className={clsx([
        'max-w-full space-y-3',
        {
          'lg:max-w-[66%]': !fullWidth,
        },
      ])}
    >
      {accordion.map((item, index) => {
        const HeadingComponent: keyof Pick<
          JSX.IntrinsicElements,
          'h2' | 'h3' | 'h4'
        > = item.title_tag.value

        return (
          <Fragment key={index}>
            <AccordionItem value={item.title}>
              <AccordionTrigger
                onClick={() => sendGTMEvent(item.title)}
                className="group w-full rounded-xl bg-gray-100 text-left hover:bg-gray-150"
              >
                <div className="flex cursor-pointer items-center justify-between space-x-4 p-4 md:items-start md:p-5">
                  <div className="flex items-center space-x-4 md:items-start md:space-x-6">
                    <div className="flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-md border border-gray-150 bg-white shadow-xs lg:size-16">
                      <Picture
                        {...item.image}
                        sizes="100px"
                        height={64}
                        width={64}
                        className="object-contain object-center"
                      />
                    </div>
                    <div className="md:mt-1">
                      <HeadingComponent className="font-bold lg:text-lg">
                        {item.title}
                      </HeadingComponent>
                      <p className="mt-1 hidden group-data-[state=open]:hidden md:block">
                        {item.preview_text}
                      </p>
                      {mdBreakpointReached && (
                        <AccordionContent className="mt-1 overflow-hidden transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                          <Content
                            content={item.text}
                            textSize="allBase"
                            className="max-w-full"
                          />
                        </AccordionContent>
                      )}
                    </div>
                  </div>
                  <CaretDown
                    size={24}
                    weight="bold"
                    className="shrink-0 text-gray-600 transition duration-150 group-data-[state=open]:rotate-180 md:mt-0.5"
                  />
                </div>
                {!mdBreakpointReached && (
                  <AccordionContent className="overflow-hidden px-4 transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                    <Content
                      content={item.text}
                      textSize="allBase"
                      className="max-w-full pb-4"
                    />
                  </AccordionContent>
                )}
                {/* SEO */}
                <Content content={item.text} className="hidden" />
              </AccordionTrigger>
            </AccordionItem>
          </Fragment>
        )
      })}
    </Accordion>
  )
}
